import { MainLayout } from '../../Layout/MainLayout';
import { Route } from '../../../routes/account.transactions_.$transactionId';
import {
  useDownloadTransactionStatementMutation,
  useTransactionQuery,
} from '../../../api/transactions';
import {
  ContentIsland,
  ErrorListItem,
  Grid,
  SectionedGridContainer,
  StatementIcon,
  ListContentIsland,
  Button,
} from '@razz-team/ui-web';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionIcon } from '../../Transaction/TransactionIcon';
import { TransactionGroupSkeleton } from './TransactionGroupSkeleton';
import { MainBreadcrumbs } from '../../Layout/MainBreadcrumbs';
import { TransactionBreakdown } from './TransactionBreakdown';
import { TransactionDetails } from './TransactionDetails';
import { TransactionMoney } from './TransactionMoney';
import { TransactionLabel } from './TransactionLabel';
import { isTransactionRepeatable, RepeatTransactionButton } from './RepeatTransactionButton';

export function TransactionPage() {
  const { createdAt } = Route.useSearch();
  const { transactionId } = Route.useParams();
  const { t } = useTranslation();
  const downloadStatementMutation = useDownloadTransactionStatementMutation();
  const transactionQuery = useTransactionQuery({ transactionId: transactionId, createdAt });
  const transaction = transactionQuery.data;

  const handleDownload = useCallback(() => {
    downloadStatementMutation.mutate({ createdAt, transactionId });
  }, [createdAt, downloadStatementMutation, transactionId]);

  return (
    <MainLayout
      title={
        <MainBreadcrumbs
          title={t('Transaction')}
          loading={transactionQuery.isPending}
          parent={{
            to: '/account/transactions',
            title: t('Transactions'),
          }}
        />
      }
    >
      {transactionQuery.error ? (
        <Grid size={12}>
          <ListContentIsland>
            <ErrorListItem error={transactionQuery.error} />
          </ListContentIsland>
        </Grid>
      ) : transactionQuery.isPending ? (
        <Grid size={12}>
          <ContentIsland>
            <TransactionGroupSkeleton />
          </ContentIsland>
        </Grid>
      ) : transaction ? (
        <>
          <Grid size={12}>
            <ContentIsland title={<TransactionLabel trx={transaction} />}>
              <SectionedGridContainer alignItems="center" justifyContent="space-between">
                <Grid gap={2} alignItems="center" display="flex">
                  <TransactionIcon transaction={transaction} />
                  <TransactionMoney trx={transaction} />
                </Grid>
                <Grid gap={2}>
                  <Button
                    color="secondary"
                    onClick={handleDownload}
                    loading={downloadStatementMutation.isPending}
                    endIcon={<StatementIcon />}
                  >
                    {t('Statement')}
                  </Button>
                  {isTransactionRepeatable(transaction) ? (
                    <RepeatTransactionButton transaction={transaction} />
                  ) : null}
                </Grid>
              </SectionedGridContainer>
            </ContentIsland>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <ContentIsland title={t('Details')}>
              <TransactionDetails trx={transaction} />
            </ContentIsland>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <ContentIsland title={t('Breakdown')}>
              <TransactionBreakdown trx={transaction} />
            </ContentIsland>
          </Grid>
        </>
      ) : null}
    </MainLayout>
  );
}
